import {
  type JSX,
  type KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { ActionIcon, Badge, Collapse, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '@/components/user/UserAvatar/UserAvatar';
import { ICON_SIZE_SM } from '@/constants/ui';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';

import type {
  OperationHistoryConfig,
  OperationHistoryEvent,
} from '../OperationHistory.types';
import type { EventGroup } from '../hooks/useOperationHistoryGrouping';

import { OperationHistoryDiff } from './OperationHistoryDiff';
import styles from './OperationHistoryEventGroup.module.css';

interface OperationHistoryEventGroupProps<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
> {
  group: EventGroup<TEvent>;
  config: OperationHistoryConfig<TEvent>;
  defaultExpanded?: boolean;
}

const CHEVRON_ICON_SIZE = 16;

export function OperationHistoryEventGroup<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
>({
  group,
  config,
  defaultExpanded = false,
}: OperationHistoryEventGroupProps<TEvent>): JSX.Element {
  const { t } = useTranslation();
  const { formatEventTime } = useLocalizedDate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const firstEvent = group.events[0];
  const color = firstEvent ? config.getEventColor(firstEvent) : 'gray';
  const EventIcon = firstEvent ? config.getEventIcon(firstEvent) : () => null;
  const groupSummary = firstEvent ? config.getEventSummary(firstEvent) : null;

  const formattedTime = useMemo(
    () => formatEventTime(group.timestamp),
    [formatEventTime, group.timestamp]
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded((previous) => !previous);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded]
  );

  return (
    <div className={styles.row}>
      <div className={styles.dotWrapper}>
        <div className={styles.dot} data-event-color={color}>
          <EventIcon size={ICON_SIZE_SM} />
        </div>
        <Badge className={styles.countBadge} size="xs" variant="filled" circle>
          {group.events.length}
        </Badge>
      </div>
      <div className={styles.content}>
        <div
          className={styles.header}
          onClick={toggleExpanded}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
        >
          <UserAvatar
            displayName={group.authorName}
            avatarUrl={group.authorAvatarUrl}
            size="xs"
          />
          <Text size="sm" fw={500}>
            {group.authorName}
          </Text>
          <span className={styles.summary}>{groupSummary}</span>
          <Text size="xs" c="dimmed">
            {t('history.group.changeCount', { count: group.events.length })}
          </Text>
          <Text size="xs" c="dimmed" className={styles.time}>
            {formattedTime}
          </Text>
          <ActionIcon variant="subtle" color="gray" size="sm">
            {isExpanded ? (
              <IconChevronUp size={CHEVRON_ICON_SIZE} />
            ) : (
              <IconChevronDown size={CHEVRON_ICON_SIZE} />
            )}
          </ActionIcon>
        </div>
        <Collapse expanded={isExpanded}>
          <div className={styles.childEvents}>
            {group.events.map((event) => {
              const eventDiffs = config.getEventDiff(event);
              return (
                <div key={event.id} className={styles.childEvent}>
                  <span className={styles.childSummary}>
                    {config.getEventSummary(event)}
                  </span>
                  {eventDiffs && <OperationHistoryDiff diffs={eventDiffs} />}
                </div>
              );
            })}
          </div>
        </Collapse>
      </div>
    </div>
  );
}
